import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { attendanceApi } from '../../api/attendanceApi';
import { AttendanceRecord } from '../../types';

interface AttendanceState {
  records: AttendanceRecord[];
  loading: boolean;
  error: string | null;
  checkInStatus: 'idle' | 'loading' | 'success' | 'error';
  checkOutStatus: 'idle' | 'loading' | 'success' | 'error';
}

const initialState: AttendanceState = {
  records: [],
  loading: false,
  error: null,
  checkInStatus: 'idle',
  checkOutStatus: 'idle',
};

export const fetchAttendance = createAsyncThunk(
  'attendance/fetch',
  async ({ employeeId, startDate, endDate }: { employeeId: string; startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      return await attendanceApi.getByEmployee(employeeId, startDate, endDate);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch attendance');
    }
  }
);

export const checkIn = createAsyncThunk(
  'attendance/checkIn',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      return await attendanceApi.checkIn(employeeId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Check-in failed');
    }
  }
);

export const checkOut = createAsyncThunk(
  'attendance/checkOut',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      await attendanceApi.checkOut(employeeId);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Check-out failed');
    }
  }
);

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    resetCheckStatus: (state) => {
      state.checkInStatus = 'idle';
      state.checkOutStatus = 'idle';
    },
    clearAttendanceError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendance.fulfilled, (state, action: PayloadAction<AttendanceRecord[]>) => {
        state.loading = false;
        state.records = action.payload;
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Check-in
      .addCase(checkIn.pending, (state) => {
        state.checkInStatus = 'loading';
      })
      .addCase(checkIn.fulfilled, (state) => {
        state.checkInStatus = 'success';
      })
      .addCase(checkIn.rejected, (state, action) => {
        state.checkInStatus = 'error';
        state.error = action.payload as string;
      })
      // Check-out
      .addCase(checkOut.pending, (state) => {
        state.checkOutStatus = 'loading';
      })
      .addCase(checkOut.fulfilled, (state) => {
        state.checkOutStatus = 'success';
      })
      .addCase(checkOut.rejected, (state, action) => {
        state.checkOutStatus = 'error';
        state.error = action.payload as string;
      });
  },
});

export const { resetCheckStatus, clearAttendanceError } = attendanceSlice.actions;
export default attendanceSlice.reducer;
